/**
 * Conta Digital proposalSituation catalog (status_id 1–5).
 * Mirrors Baas `PaySmartProposalSituationCatalog.ToVerificationStatus`.
 */

export const PAYSMART_PROPOSAL_SITUATION = {
  AWAITING_AUTOMATIC: 1,
  AWAITING_MANUAL: 2,
  APPROVED_MANUAL: 3,
  APPROVED_AUTOMATIC: 4,
  REJECTED_MANUAL: 5,
};

/** Hide fake PLD / "Aguardando análise automática" — product has no PLD yet. */
export const isUnavailablePldProposalSituation = (situation) => {
  if (!situation) return false;
  const id = Number(situation.statusId ?? situation.status_id);
  if (id === PAYSMART_PROPOSAL_SITUATION.AWAITING_AUTOMATIC) return true;
  const desc = String(situation.description || situation.statusName || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return desc.includes("analise automatica");
};

/** status_id → BaaS verification_status (PENDING / APPROVED / REJECTED). */
export const mapProposalSituationToVerificationStatus = (statusId) => {
  const id = Number(statusId);
  if (id === PAYSMART_PROPOSAL_SITUATION.AWAITING_AUTOMATIC
    || id === PAYSMART_PROPOSAL_SITUATION.AWAITING_MANUAL) {
    return "PENDING";
  }
  if (id === PAYSMART_PROPOSAL_SITUATION.APPROVED_MANUAL
    || id === PAYSMART_PROPOSAL_SITUATION.APPROVED_AUTOMATIC) {
    return "APPROVED";
  }
  if (id === PAYSMART_PROPOSAL_SITUATION.REJECTED_MANUAL) {
    return "REJECTED";
  }
  return null;
};

/** Badge tone: pending (1–2), approved (3–4), rejected (5). */
export const mapProposalSituationToTone = (statusId) => {
  const verification = mapProposalSituationToVerificationStatus(statusId);
  if (verification === "APPROVED") return "approved";
  if (verification === "REJECTED") return "rejected";
  if (verification === "PENDING") return "pending";
  return null;
};

/**
 * Contas UI status when derived from proposal (not entity KYC alone).
 * REJECTED → blocked (red); PENDING → pending (amber); APPROVED → active.
 */
export const mapProposalSituationToAccountStatus = (statusId) => {
  const verification = mapProposalSituationToVerificationStatus(statusId);
  if (verification === "APPROVED") return "active";
  if (verification === "REJECTED") return "blocked";
  if (verification === "PENDING") return "pending";
  return null;
};

const isTruthyFlag = (value) =>
  value === true || value === 1 || value === "1" || String(value).toLowerCase() === "true";

/**
 * Reconstruct proposal situation from list/open-account Account flags
 * (`document_pending`, `anti_fraud_analysis`, `status_name`) when
 * `proposal_situation` is omitted.
 */
export const inferProposalSituationFromAccountFlags = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const documentPending = isTruthyFlag(
    raw.document_pending ?? raw.documentPending ?? raw.DocumentPending,
  );
  const antiFraudAnalysis = isTruthyFlag(
    raw.anti_fraud_analysis ?? raw.antiFraudAnalysis ?? raw.AntiFraudAnalysis,
  );
  const statusNameRaw = raw.status_name ?? raw.statusName ?? raw.StatusName ?? null;
  const statusName =
    statusNameRaw != null && String(statusNameRaw).trim()
      ? String(statusNameRaw).trim()
      : null;

  let statusId = null;
  if (statusName) {
    statusId = tryParseProposalStatusIdFromDescription(statusName);
  }

  if (statusId == null) {
    if (documentPending && antiFraudAnalysis) {
      statusId = PAYSMART_PROPOSAL_SITUATION.AWAITING_AUTOMATIC;
    } else if (documentPending && !antiFraudAnalysis && statusName) {
      statusId = PAYSMART_PROPOSAL_SITUATION.REJECTED_MANUAL;
    } else if (documentPending || antiFraudAnalysis) {
      // Legacy / mid-provision flags without StatusName — still pending (never Active).
      statusId = PAYSMART_PROPOSAL_SITUATION.AWAITING_AUTOMATIC;
    }
  }

  if (statusId == null && isAccountLifecycleStatusName(statusName)) return null;

  if (statusId == null && !statusName) return null;

  return normalizeProposalSituation({
    status_id: statusId,
    description: statusName,
  });
};

const compactStatusKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-_\s]+/g, "");

export const isAccountLifecycleStatusName = (description) => {
  const compact = compactStatusKey(description);
  return (
    compact === "bloqueado"
    || compact === "blocked"
    || compact === "locked"
    || compact === "desativado"
    || compact === "deactivated"
    || compact === "inativo"
    || compact === "inactive"
    || compact === "suspenso"
    || compact === "suspended"
  );
};

/** Best-effort status_id from partner description (mirrors Baas catalog). */
export const tryParseProposalStatusIdFromDescription = (description) => {
  if (description == null || !String(description).trim()) return null;
  const trimmed = String(description).trim();
  const compact = compactStatusKey(trimmed);
  if (
    compact === "bloqueado"
    || compact === "blocked"
    || compact === "locked"
  ) {
    return null;
  }
  if (
    compact === "desbloqueado"
    || compact === "unblocked"
    || compact === "unlocked"
    || compact === "approvedautomatically"
    || compact === "approved_automatically"
  ) {
    return PAYSMART_PROPOSAL_SITUATION.APPROVED_AUTOMATIC;
  }
  if (compact === "awaitingmanualanalysis" || compact === "aguardandoanalisemanual") {
    return PAYSMART_PROPOSAL_SITUATION.AWAITING_MANUAL;
  }
  if (compact === "awaitingautomaticanalysis" || compact === "aguardandoanaliseautomatica") {
    return PAYSMART_PROPOSAL_SITUATION.AWAITING_AUTOMATIC;
  }
  if (compact === "approvedmanually") {
    return PAYSMART_PROPOSAL_SITUATION.APPROVED_MANUAL;
  }
  if (compact === "rejectedmanually") {
    return PAYSMART_PROPOSAL_SITUATION.REJECTED_MANUAL;
  }

  const lower = trimmed.toLowerCase();
  if (lower.includes("reprov") || lower.includes("reject")) {
    return PAYSMART_PROPOSAL_SITUATION.REJECTED_MANUAL;
  }
  if (lower.includes("aprov") || lower.includes("approv")) {
    return lower.includes("autom") || lower.includes("automatic")
      ? PAYSMART_PROPOSAL_SITUATION.APPROVED_AUTOMATIC
      : PAYSMART_PROPOSAL_SITUATION.APPROVED_MANUAL;
  }
  if (lower.includes("manual")) {
    return PAYSMART_PROPOSAL_SITUATION.AWAITING_MANUAL;
  }
  if (
    lower.includes("autom")
    || lower.includes("automatic")
    || lower.includes("anális")
    || lower.includes("analis")
    || lower.includes("aguard")
    || lower.includes("pending")
    || lower.includes("review")
  ) {
    return PAYSMART_PROPOSAL_SITUATION.AWAITING_AUTOMATIC;
  }
  return null;
};

/** Normalize `proposal_situation` from BaaS (snake_case / camelCase). */
export const normalizeProposalSituation = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const statusIdRaw = raw.status_id ?? raw.statusId ?? raw.StatusId ?? null;
  const descriptionRaw = raw.description ?? raw.Description ?? null;
  if (statusIdRaw == null && (descriptionRaw == null || String(descriptionRaw).trim() === "")) {
    return null;
  }

  const statusId = statusIdRaw != null ? Number(statusIdRaw) : null;
  let id = Number.isFinite(statusId) ? statusId : null;
  const description =
    descriptionRaw != null && String(descriptionRaw).trim()
      ? String(descriptionRaw).trim()
      : null;

  // Description-only payloads (missing status_id) must still drive Conta ≠ Ativa.
  if (id == null && description) {
    id = tryParseProposalStatusIdFromDescription(description);
  }

  return {
    statusId: id,
    description,
    verificationStatus: mapProposalSituationToVerificationStatus(id),
    tone: mapProposalSituationToTone(id),
    accountStatus: mapProposalSituationToAccountStatus(id),
  };
};

/**
 * Resolve display proposal situation: explicit payload, else Account flags.
 * @param {object|null} proposalSituationRaw
 * @param {object|null} accountRaw — OpenAccountResponse / mapped account fields
 */
export const resolveProposalSituation = (proposalSituationRaw, accountRaw = null) =>
  normalizeProposalSituation(proposalSituationRaw)
  || inferProposalSituationFromAccountFlags(accountRaw);

/**
 * True when Contas/Entidades already have enough local signals to render Conta
 * status without calling PaySmart `proposal/last` (list/detail DB flags,
 * verification_status, operational status, or proposalSituation).
 */
export const hasLocalProposalContext = (accountOrEntity) => {
  if (!accountOrEntity || typeof accountOrEntity !== "object") return false;

  if (resolveProposalSituation(
    accountOrEntity.proposalSituation
      ?? accountOrEntity.proposal_situation
      ?? null,
    accountOrEntity,
  )) {
    return true;
  }

  const verification = String(
    accountOrEntity.verificationStatus
      ?? accountOrEntity.verification_status
      ?? "",
  )
    .trim()
    .toUpperCase();
  if (verification === "APPROVED" || verification === "PENDING" || verification === "REJECTED") {
    return true;
  }

  const status = String(accountOrEntity.status || "").trim().toLowerCase();
  if (
    status === "active"
    || status === "pending"
    || status === "blocked"
    || status === "suspended"
    || status === "deactivated"
    || status === "none"
  ) {
    return true;
  }

  if (accountOrEntity.canTransact === true) return true;

  const statusName = accountOrEntity.statusName ?? accountOrEntity.status_name ?? null;
  if (statusName != null && String(statusName).trim()) return true;

  if (
    isTruthyFlag(accountOrEntity.documentPending ?? accountOrEntity.document_pending)
    || isTruthyFlag(accountOrEntity.antiFraudAnalysis ?? accountOrEntity.anti_fraud_analysis)
  ) {
    return true;
  }

  return false;
};

/**
 * Unified Contas operational status for grid + detail.
 * Prefer proposal-derived status; else map verification_status / UI status.
 *
 * @param {object} [options]
 * @param {boolean} [options.ignoreEntityKycFallback] When true (entities console), never
 *   treat entity KYC APPROVED as Conta "active". Without Account flags → "none" (Sem conta).
 */
export const resolveAccountOperationalStatus = ({
  proposalSituation = null,
  verificationStatus = null,
  status = null,
  documentPending = null,
  antiFraudAnalysis = null,
  statusName = null,
  raw = null,
  ignoreEntityKycFallback = false,
  lifecycle = null,
} = {}) => {
  let situation = null;
  if (proposalSituation && typeof proposalSituation === "object") {
    // Accept already-normalized or raw snake_case / partial payloads.
    situation =
      proposalSituation.accountStatus != null
        ? proposalSituation
        : normalizeProposalSituation(proposalSituation);
  }

  if (!situation?.accountStatus) {
    situation =
      resolveProposalSituation(
        situation
          ? { status_id: situation.statusId, description: situation.description }
          : null,
        {
          document_pending: documentPending,
          anti_fraud_analysis: antiFraudAnalysis,
          status_name: statusName,
          ...(raw && typeof raw === "object" ? raw : {}),
        },
      ) || situation;
  }

  const life = String(
    lifecycle
    || raw?.lifecycle
    || "",
  ).trim().toLowerCase();
  if (life === "deactivated" || life === "suspended" || life === "blocked") {
    return {
      status: life,
      proposalSituation: situation,
      canTransact: false,
    };
  }
  if (isAccountLifecycleStatusName(statusName || raw?.status_name || raw?.statusName)) {
    const compact = compactStatusKey(statusName || raw?.status_name || raw?.statusName);
    const accountStatus =
      compact === "suspenso" || compact === "suspended"
        ? "suspended"
        : compact === "desativado" || compact === "deactivated" || compact === "inativo" || compact === "inactive"
          ? "deactivated"
          : "blocked";
    return {
      status: accountStatus,
      proposalSituation: situation,
      canTransact: false,
    };
  }

  if (situation?.accountStatus) {
    return {
      status: situation.accountStatus,
      proposalSituation: situation,
      canTransact: situation.verificationStatus === "APPROVED",
    };
  }

  if (situation?.verificationStatus === "PENDING" || situation?.tone === "pending") {
    return {
      status: "pending",
      proposalSituation: situation,
      canTransact: false,
    };
  }

  const pendingFlags =
    isTruthyFlag(documentPending)
    || isTruthyFlag(antiFraudAnalysis)
    || isTruthyFlag(raw?.document_pending ?? raw?.documentPending)
    || isTruthyFlag(raw?.anti_fraud_analysis ?? raw?.antiFraudAnalysis);

  if (ignoreEntityKycFallback) {
    if (pendingFlags) {
      return { status: "pending", proposalSituation: situation, canTransact: false };
    }
    if (situation?.verificationStatus === "REJECTED") {
      return { status: "blocked", proposalSituation: situation, canTransact: false };
    }
    // No Valut Account / Conta Digital signal — Sem conta (never Ativa / Em análise from KYC).
    return {
      status: "none",
      proposalSituation: situation,
      canTransact: false,
    };
  }

  const verification = String(verificationStatus || status || "").toUpperCase();
  let resolved = "pending";
  if (verification === "APPROVED" || verification === "ACTIVE" || status === "active") {
    resolved = "active";
  } else if (
    verification === "BLOCKED"
    || verification === "SUSPENDED"
    || verification === "REJECTED"
    || status === "blocked"
  ) {
    resolved = "blocked";
  } else if (status === "pending" || verification === "PENDING" || verification.includes("REVIEW")) {
    resolved = "pending";
  }

  if (pendingFlags && resolved === "active") {
    resolved = "pending";
  }

  return {
    status: resolved,
    proposalSituation: situation,
    canTransact: resolved === "active" && !pendingFlags,
  };
};

/**
 * Contas "Consultar proposta": block when processor status_id is 4
 * (Aprovada automaticamente). Pending / rejected / manual-approved stay enabled.
 */
export const canConsultDigitalProposal = (proposalSituation) => {
  const situation =
    proposalSituation && typeof proposalSituation === "object" && proposalSituation.statusId != null
      ? proposalSituation
      : normalizeProposalSituation(proposalSituation);
  const statusId = Number(situation?.statusId);
  return statusId !== PAYSMART_PROPOSAL_SITUATION.APPROVED_AUTOMATIC;
};

/** True when console may enable transfer / Pix CTAs for this account. */
export const canAccountTransact = (account) => {
  if (!account || typeof account !== "object") return false;
  const resolved = resolveAccountOperationalStatus({
    proposalSituation: account.proposalSituation || null,
    verificationStatus: account.verificationStatus || account.raw?.verification_status,
    status: account.status,
    documentPending: account.documentPending,
    antiFraudAnalysis: account.antiFraudAnalysis,
    statusName: account.statusName,
    raw: account.raw || account,
  });
  return resolved.canTransact === true;
};

/** Normalize PaySmart `personal_data` from GET entity proposal. */
export const mapBaasProposalPersonalDataToUi = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const dddRaw = raw.local_area_code_cell_phone
    ?? raw.localAreaCodeCellPhone
    ?? raw.LocalAreaCodeCellPhone
    ?? null;
  const phoneRaw = raw.cell_phone_number
    ?? raw.cellPhoneNumber
    ?? raw.CellPhoneNumber
    ?? null;

  return {
    personalDocument:
      raw.personal_document ?? raw.personalDocument ?? raw.PersonalDocument ?? null,
    personalName:
      raw.personal_name ?? raw.personalName ?? raw.PersonalName ?? null,
    personalEmail:
      raw.personal_email ?? raw.personalEmail ?? raw.PersonalEmail ?? null,
    birthDate: raw.birth_date ?? raw.birthDate ?? raw.BirthDate ?? null,
    motherName: raw.mother_name ?? raw.motherName ?? raw.MotherName ?? null,
    localAreaCodeCellPhone:
      dddRaw != null && dddRaw !== "" ? Number(dddRaw) : null,
    cellPhoneNumber:
      phoneRaw != null && phoneRaw !== "" ? Number(phoneRaw) : null,
  };
};

/** Map GET `/v1/entities/{id}/proposal` (or open-account fields) to UI model. */
export const mapBaasEntityProposalToUi = (data) => {
  if (!data || typeof data !== "object") return null;

  const situation = normalizeProposalSituation(
    data.proposal_situation ?? data.proposalSituation ?? data.ProposalSituation,
  );
  const proposalIdRaw = data.proposal_id ?? data.proposalId ?? data.ProposalId ?? null;
  const proposalId = proposalIdRaw != null && proposalIdRaw !== ""
    ? Number(proposalIdRaw)
    : null;
  const personalData = mapBaasProposalPersonalDataToUi(
    data.personal_data ?? data.personalData ?? data.PersonalData,
  );
  const creditEngineRaw =
    data.credit_engine_info ?? data.creditEngineInfo ?? data.CreditEngineInfo ?? null;
  const creditEngine = creditEngineRaw && typeof creditEngineRaw === "object"
    ? {
        status: creditEngineRaw.status ?? creditEngineRaw.Status ?? null,
        message: creditEngineRaw.message ?? creditEngineRaw.Message ?? null,
        approved: creditEngineRaw.approved ?? creditEngineRaw.Approved ?? null,
        score: creditEngineRaw.score ?? creditEngineRaw.Score ?? null,
      }
    : null;

  if (
    !situation
    && (proposalId == null || !Number.isFinite(proposalId))
    && !personalData
  ) {
    return null;
  }

  return {
    entityId: data.entity_id ?? data.entityId ?? null,
    nationalId: data.national_id ?? data.nationalId ?? null,
    proposalId: Number.isFinite(proposalId) ? proposalId : null,
    situation,
    personalData,
    creditEngine,
    raw: data,
  };
};
